import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button, Popover, OverlayTrigger, Badge, Table, Alert } from '../../ui-kit';
import PeriodSelector from '../../../utils/forms/PeriodSelector';
import DefaultReactTable from '../../../utils/react-table';
import PaginatedLoader from '../../general/PaginatedLoader';
import './GroupReport.css';
import round from '../../../utils/round';
import { operations as workingTimeDirectiveReportOperations } from '../../../state/modules/working-time-directive-reports';
import { selectors as workingTimeDirectiveReportSelectors } from '../../../state/modules/working-time-directive-reports';

const badgeVariants = {
  yellow: 'warning',
  green: 'success',
  red: 'danger'
};

class EvaluationDetails extends React.PureComponent {
  static whyDidYouRender = true;

  render() {
    const { rules, evaluations } = this.props;

    if (!evaluations || Object.entries(evaluations).length === 0) return I18n.t('no_results_found');
    return (
      <Table bordered condensed>
        <tbody>
          {rules.map(rule => (
            <tr key={`${rule.id}`}>
              <td>
                <strong>{rule.name}</strong>
                {evaluations[rule.id].description && (
                  <WTDEvaluationDescription descriptions={evaluations[rule.id].description} />
                )}
              </td>
              <td className="number-column">
                <h5>
                  <Badge variant={badgeVariants[evaluations[rule.id]?.status] || 'secondary'}>
                    {round(evaluations[rule.id].value, 2)}
                  </Badge>
                </h5>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }
}

class WTDEvaluationDescription extends React.PureComponent {
  static whyDidYouRender = true;
  render() {
    return (
      <ul>
        {this.props.descriptions.map((description, index) => (
          <li className="wtd-rule-evaluation-description" key={index}>
            {description}
          </li>
        ))}
      </ul>
    );
  }
}

class WTDIntervalCellComponent extends React.PureComponent {
  static whyDidYouRender = true;
  render() {
    const { userInterval, rules } = this.props;

    return (
      <OverlayTrigger
        placement="auto-end"
        overlay={
          <Popover id="popover-positioned-right" className="d-flex flex-column flex-wrap popover">
            <Popover.Header>{I18n.t('details')}</Popover.Header>
            <Popover.Body>
              <EvaluationDetails rules={rules} evaluations={userInterval?.rule_evaluations} />
            </Popover.Body>
          </Popover>
        }
      >
        <div className={`wtd-cell ${userInterval?.status}`} />
      </OverlayTrigger>
    );
  }
}

class GroupReport extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      startTime: props.default_start_time,
      endTime: props.default_end_time
    };

    this.fetchData();
  }

  columns = () => [
    {
      id: 'name',
      Header: I18n.t('attributes.name'),
      accessor: d => `${d.user.first_name} ${d.user.last_name}`,
      width: 130,
      Cell: row => {
        return (
          <a href={`/users/${row.original.user.id}`}>
            {row.original.user.first_name} {row.original.user.last_name}
          </a>
        );
      }
    },
    {
      Header: I18n.t('week.name').capitalizeFirstLetter(),
      columns: this.getIntervals().map((interval, index) => {
        return {
          Header: interval.weekNo,
          width: 35,
          filterable: false,
          Cell: row => {
            let userInterval = row.original.intervals[index];
            return <WTDIntervalCellComponent key={index} userInterval={userInterval} rules={this.props.rules} />;
          }
        };
      })
    }
  ];

  // This method dynamically generates the column headers that will be passed
  // to the React table. For the header label we need the week number
  getIntervals = () => {
    if (this.props.userReports.length === 0) return [];

    const { intervals } = this.props.userReports[0];
    return intervals.map((interval, index) => ({
      startTime: interval.start_time,
      weekNo: moment(interval.start_time).format('W'),
      index: index
    }));
  };

  dateChange = values => {
    this.setState({
      startTime: values.start_time,
      endTime: values.end_time
    });
  };

  fetchData = () => {
    const params = {
      start_time: moment(this.state.startTime).format('YYYY-MM-DDTHH:mm:ss'),
      end_time: moment(this.state.endTime).format('YYYY-MM-DDTHH:mm:ss')
    };

    this.props.fetchWorkingTimeDirectiveReports({ ids: this.props.id, params });
  };

  render = () => {
    return (
      <React.Fragment>
        <PeriodSelector
          startTime={this.state.startTime}
          endTime={this.state.endTime}
          onChange={this.dateChange}
          horizontal={true}
        />
        <Button variant="primary" onClick={() => this.fetchData()}>
          {I18n.t('show')}
        </Button>
        <br />
        <br />
        <PaginatedLoader {...this.props.paginationInfo} />
        <br />
        {!this.props.error && (
          <DefaultReactTable
            filterable
            resizable={false}
            showPagination={true}
            className="report-table wtd-report"
            loading={this.props.loading}
            data={this.props.userReports}
            columns={this.columns()}
            defaultPageSize={25}
            defaultSorted={[{ id: 'name' }]}
          />
        )}
        {this.props.error && <Alert variant="danger">{this.props.error.statusText}</Alert>}
      </React.Fragment>
    );
  };
}

const mapStateToProps = state => ({
  loading: workingTimeDirectiveReportSelectors.loading(state),
  userReports: workingTimeDirectiveReportSelectors.values(state),
  rules: workingTimeDirectiveReportSelectors.rules(state),
  error: workingTimeDirectiveReportSelectors.error(state),
  paginationInfo: workingTimeDirectiveReportSelectors.paginationInfo(state)
});

const mapDispatchToProps = {
  fetchWorkingTimeDirectiveReports: workingTimeDirectiveReportOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupReport);
